import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { skipToken } from '@apollo/client/react';
import type { ExperienceType, OdProtosSellCommon_Product } from '__generated__/globalTypes';
import { useAuthenticatedQuery } from 'platform/apollo';
import type { AvailableProduct } from 'sellDirect/declarations/products';
import { isAvailableProduct, Product as ProtoProduct } from 'sellDirect/declarations/products';

import type {
  SellerProductsExperiencesQuery_sellerProducts_SellerProductsQuery_productsExperiences_SellerProductExperienceResponse_products as Product,
  SellerProductsExperiencesQuery_sellerProducts_SellerProductsQuery_productsExperiences_SellerProductExperienceResponse as ProductsExperiences,
} from './__generated__/useGetSellerProductsExperiences';
import { SellerProductsExperiencesDocument } from './__generated__/useGetSellerProductsExperiences';

type CashOfferProduct = Extract<Product, { __typename: 'SellDirectProduct' }>;
type CashPlusProduct = Extract<Product, { __typename: 'SellWithUpsideProduct' }>;
type ListingProduct = Extract<Product, { __typename: 'ListingProduct' }>;

/** Seller products data with individual product fields and available products list */
export interface SellerProducts {
  /** List of displayable product kinds the customer qualified for (Cash and Cash Plus. List is not a displayable product) */
  availableProducts: AvailableProduct[];
  /** The product the customer has selected (if any) */
  selectedProduct?: AvailableProduct;

  /** Listing product data, if customer qualified */
  listingProduct?: ListingProduct;
  /** Cash offer product data, if customer qualified */
  cashOfferProduct?: CashOfferProduct;
  /** Cash Plus product data, if customer qualified */
  cashPlusProduct?: CashPlusProduct;
  /** Experiences - deprecated, used for ALO still */
  experiences: ExperienceType[];
  /** Products the seller was previously eligible for but is no longer */
  previouslyEligibleProducts: OdProtosSellCommon_Product[];
}

const transformToSellerProducts = (
  productsExperiences: ProductsExperiences | undefined,
): SellerProducts => {
  const products = productsExperiences?.products ?? [];
  const selectedKind = productsExperiences?.selectedProduct?.kind;
  const experiences = productsExperiences?.experiences ?? [];

  return {
    availableProducts: products.map((p) => p.kind).filter(isAvailableProduct),
    selectedProduct: selectedKind && isAvailableProduct(selectedKind) ? selectedKind : undefined,
    listingProduct: products.find(
      (p): p is ListingProduct => p.kind === ProtoProduct.LIST_WITH_CERTAINTY,
    ),
    cashOfferProduct: products.find(
      (p): p is CashOfferProduct => p.kind === ProtoProduct.OPENDOOR_DIRECT,
    ),
    cashPlusProduct: products.find(
      (p): p is CashPlusProduct => p.kind === ProtoProduct.SELL_WITH_UPSIDE,
    ),
    experiences,
    previouslyEligibleProducts: productsExperiences?.previouslyEligibleProducts ?? [],
  };
};

/** Hook to get the products experiences for a seller lead */
export const useGetSellerProductsExperiences = (sellerLeadUuid: string | undefined) => {
  const { data, loading, error, refetch } = useAuthenticatedQuery(
    SellerProductsExperiencesDocument,
    !sellerLeadUuid
      ? skipToken
      : {
          variables: { sellerLeadUuid },
        },
  );

  const rawData = data?.sellerProducts?.productsExperiences;
  const sellerProducts = useMemo(() => transformToSellerProducts(rawData), [rawData]);

  return {
    sellerProducts,
    loading,
    error,
    refetch,
  };
};

// --- Query ---

gql`
  query SellerProductsExperiences($sellerLeadUuid: ID!) {
    sellerProducts {
      productsExperiences(sellerLeadUuid: $sellerLeadUuid) {
        sellerLeadUuid
        addressToken
        experiences
        previouslyEligibleProducts
        products {
          ...SellerProduct
        }
        selectedProduct {
          ...SellerProduct
          kind
        }
      }
    }
  }

  fragment SellerProduct on Product {
    id
    kind
    __typename

    ... on SellDirectProduct {
      token
      offerUuid

      pricing {
        ...SellerCashOfferDetails
      }
    }

    ... on ListingProduct {
      pricing {
        __typename
        ...SellerPricing
      }
      homeValue {
        ...SellerHomeValue
      }
      assignedAgent {
        agentId
        firstName
        lastName
        email
        phoneNumber
        photoUrl
        notes
      }
      brokerage {
        brokerageId
        name
        teamLeadAgentId
        publicDisplayPhoneNumber
        opendoorManagingHumanId
        opendoorManagingHuman {
          id
          firstName
          lastName
          email
        }
        assignmentType
        imageUrl
      }
      listingAgreementSignedAt
    }

    ... on SellWithUpsideProduct {
      selectedEstimatedSalesPriceCents
      selectedUpfrontCashCents
      pricing {
        __typename
        uuid
        agentRecommendedListPriceCents
        initialCashPaymentCents
        initialAgentFeeAdvanceCents
        netOfferPriceCents
        programFeePercentage
        programFeeCents
        programFeeMinimumCents
        projectedTotalProceedsCents
        projectedUpsideCents
        opendoorProjectedResalePriceCents
        agentProjectedResalePriceCents
        odAddendumCredit
        ...SellerPricing
      }
    }
  }

  fragment SellerHomeValue on EstimatedHomeValue {
    lowCents
    highCents
  }

  fragment SellerCashOfferDetails on CashOfferDetails {
    id
    closingCostsCents
    forecastedSalePriceCents
    headlinePriceCents
    netProceedsCents
    opendoorFeeCents
    opendoorFeePercentage
    postRepairValuationCents
    prelistRepairCostsCents
    sellerCreditAddendumCents
    sellerRepairsCostCents
  }

  fragment SellerPricing on Pricing {
    projectedSalesPriceCents
    projectedClosingCostsCents
    projectedRepairCostsCents
    projectedAgentFeesCents
    projectedAgentFeesPercentage
    projectedNetProceedsCents
  }
`;
